import type { ThreadListItem } from '@/lib/messaging/types';

/**
 * Snapshot of the prior poll's state for a single thread — just enough
 * to recognise a "new inbound arrived" event on the next poll.
 */
export interface ThreadSnapshot {
  lastMessageId: string | null;
  lastMessageAt: string;
  unreadCount: number;
}

export function takeSnapshot(t: ThreadListItem): ThreadSnapshot {
  return {
    lastMessageId: t.lastMessage?.id ?? null,
    lastMessageAt: t.lastMessageAt,
    unreadCount: t.unreadCount,
  };
}

export function snapshotsByThreadId(
  threads: readonly ThreadListItem[],
): Map<string, ThreadSnapshot> {
  const out = new Map<string, ThreadSnapshot>();
  for (const t of threads) out.set(t.id, takeSnapshot(t));
  return out;
}

/**
 * A thread has a *new* inbound arrival when:
 *   - its lastMessage is INBOUND, AND
 *   - either the message id changed since the previous snapshot,
 *     or unreadCount has increased,
 *   - and the timestamp moved forward.
 *
 * That last clause guards against out-of-order polls where we briefly see an
 * older snapshot — never want to fire a notification for an old message.
 */
export function hasNewInbound(
  current: ThreadListItem,
  previous: ThreadSnapshot | undefined,
): boolean {
  const last = current.lastMessage;
  if (!last || last.direction !== 'INBOUND') return false;
  if (!previous) {
    // First time we've seen this thread *during the session*. We only want to
    // notify if it's genuinely unread — otherwise we'd ding for every old
    // thread on the first poll after a route change.
    return current.unreadCount > 0;
  }
  if (current.lastMessageAt <= previous.lastMessageAt) return false;
  if (last.id !== previous.lastMessageId) return true;
  return current.unreadCount > previous.unreadCount;
}

/** Sum of unread counts across the non-closed threads we care about. */
export function totalUnreadCount(threads: readonly ThreadListItem[]): number {
  let total = 0;
  for (const t of threads) {
    if (t.status === 'CLOSED') continue;
    total += Number.isFinite(t.unreadCount) ? t.unreadCount : 0;
  }
  return total;
}
